import type { Answer, DocumentVerwijzing, Revisie } from '@eformulieren/model'

/**
 * De antwoordstand en de lezers erop.
 *
 * **Eén vorm voor de levende beurt en voor een revisie.** De uitgeschreven stand
 * van de lopende beurt (`GET /stand`, ontwerp 9.2) en de bevroren stand van een
 * revisie (4.5) hebben dezelfde vorm: per stabiel veld-id één antwoord, nooit een
 * pad (9.1). Het type verwijst daarom naar `Revisie['antwoorden']` in plaats van
 * dat begrip hier een tweede keer te definiëren (ontwerp 11.4).
 *
 * De lezers hieronder zijn de enige plek waar de engine in een antwoord kijkt.
 * Daarmee staat de vorm van een antwoord op één plaats in de evaluator, en niet
 * per regelsoort opnieuw.
 */

/** Per stabiel veld-id één antwoord. */
export type State = Revisie['antwoorden']

/** Een enkelvoudige waarde zoals die in de stand staat. */
export type SingleValue = string | number | boolean | null

/**
 * Een waarde waarmee een zichtbaarheidsconditie kan vergelijken: hetzelfde type
 * als `heeftEenVan` in het model, dus zonder `null`. Een leeg antwoord levert geen
 * keuzewaarde op in plaats van de waarde `null`.
 */
export type ChoiceValue = Exclude<SingleValue, null>

/**
 * De waarden van een antwoord als lijst, voor een vergelijking op "heeft een van"
 * (R-33, B11).
 *
 * Een enkelvoudig antwoord levert één waarde, of geen enkele als hij `null` is.
 * Een meerwaardig antwoord levert al zijn waarden, dus een conditie op een
 * `multiselect`, een aanvinklijst of een `tag` klopt zodra één gekozen waarde in
 * `heeftEenVan` staat. Dat is letterlijk wat B11 met "waarde X of een van X, Y"
 * beschrijft.
 *
 * Een bijlageveld en een herhaalgroep leveren er geen: een documentverwijzing en
 * een rij zijn geen keuzewaarde, en een zichtbaarheidsconditie erop zou nooit
 * kloppen.
 */
export function valuesOf(response: Answer | undefined): readonly ChoiceValue[] {
  if (response === undefined) return []
  if ('waarde' in response) return response.waarde === null ? [] : [response.waarde]
  if ('waarden' in response) return response.waarden
  return []
}

/** De enkelvoudige waarde, of undefined als het antwoord er geen heeft. */
export function singleValueOf(response: Answer | undefined): SingleValue | undefined {
  if (response !== undefined && 'waarde' in response) return response.waarde
  return undefined
}

/**
 * De documentverwijzingen van een bijlageveld (U25, R-199), of undefined bij een
 * ander antwoord.
 *
 * Elke verwijzing draagt de hash, de bestandsnaam en de omvang. Die laatste twee
 * staan er omdat `bestandseisen` typen en maximale grootte moet kunnen toetsen
 * zonder de opslag te bevragen: de evaluator is een pure functie zonder I/O per
 * regel (ontwerp 5.5) en draait ook in de browser (5.9).
 */
export function documentsOf(response: Answer | undefined): readonly DocumentVerwijzing[] | undefined {
  if (response !== undefined && 'documenten' in response) return response.documenten
  return undefined
}

/** De rijen van een herhaalgroep, of undefined bij een ander antwoord. */
export function rowsOf(response: Answer | undefined): Extract<Answer, { rijen: unknown }>['rijen'] | undefined {
  if (response !== undefined && 'rijen' in response) return response.rijen
  return undefined
}

/**
 * Of een rij van een herhaalgroep leeg is: geen enkele waarde erin die niet leeg is (besluit van 30
 * oktober 2026). Zo'n rij telt niet mee voor een minimum aantal rijen.
 */
export function rowIsEmpty(row: { readonly waarden: Readonly<Record<string, unknown>> }): boolean {
  return Object.values(row.waarden).every((value) => isEmpty(value as Answer | undefined))
}

/**
 * Het aantal dingen in een antwoord: rijen bij een herhaalgroep voor
 * `minimumAantal` en `maximumAantal` (R-42), bestanden bij een bijlageveld voor
 * `bestandseisen` (R-104).
 *
 * Undefined betekent: dit antwoord heeft geen aantal. Een controle op het aantal
 * hoort dan niet op dit veld te staan, en de definitie houdt dat al tegen door
 * het doel aan `MULTI_VALUED` te toetsen.
 */
export function inCount(response: Answer | undefined): number | undefined {
  if (response === undefined) return undefined
  const rows = rowsOf(response)
  if (rows !== undefined) return rows.length
  if ('waarden' in response) return response.waarden.length
  const documents = documentsOf(response)
  if (documents !== undefined) return documents.length
  return undefined
}

/**
 * Of een antwoord leeg is, en dus of `verplicht` faalt (R-26).
 *
 * Een veld dat de invuller niet heeft aangeraakt staat niet in de stand: leeg is
 * geen waarde, en een `slider` zonder aanraking staat niet stil op 5 (ontwerp
 * 6.2). Daarom is `undefined` leeg en zijn `false` en `0` dat niet: die staan er
 * omdat iemand ze heeft gekozen.
 *
 * Alleen witruimte is ook leeg. Zonder die regel voldoet een spatie aan een
 * verplicht rich-text-veld.
 */
export function isEmpty(response: Answer | undefined): boolean {
  if (response === undefined) return true
  const rows = rowsOf(response)
  if (rows !== undefined) return rows.length === 0
  // Wie alles weer uitvinkt laat een lege lijst achter, en dat is net zo leeg als
  // een veld dat er niet staat.
  if ('waarden' in response) return response.waarden.length === 0
  const documents = documentsOf(response)
  if (documents !== undefined) return documents.length === 0
  const value = singleValueOf(response)
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  return false
}

/**
 * De tekst van een antwoord, voor de tellers en de termencontrole.
 *
 * Rich text komt als tekst in de stand binnen: leesbaar zonder Yjs (ontwerp 4.5).
 * Een bijlageveld en een herhaalgroep leveren geen tekst; een tekencontrole erop
 * zou een hash of een rij-id gaan meten.
 */
export function textOf(response: Answer | undefined): string | undefined {
  const value = singleValueOf(response)
  if (value === undefined || value === null) return undefined
  return typeof value === 'string' ? value : String(value)
}

/**
 * Alle echte tekstfragmenten van een antwoord, voor de termencontrole en voor de
 * woordendrempel van een sectie.
 *
 * Anders dan `tekstVan` schrijft dit een getal of een boolean niet uit: een
 * aangevinkte toggle is geen woord, en zou de woordentelling van een sectie
 * vervuilen. Van een herhaalgroep komen de teksten uit de rijen mee, want de
 * inhoud van een sectie zit daar ook in.
 */
export function textsOf(response: Answer | undefined): readonly string[] {
  if (response === undefined) return []
  const rows = rowsOf(response)
  if (rows !== undefined) {
    return rows.flatMap((row) => Object.values(row.waarden).flatMap((value) => textsOf(value)))
  }
  if ('waarden' in response) return response.waarden.filter((value): value is string => typeof value === 'string')
  if ('waarde' in response) return typeof response.waarde === 'string' ? [response.waarde] : []
  return []
}

/**
 * Het aantal tekens (R-12).
 *
 * Codepunten en niet UTF-16-eenheden: `'𝑥'.length` is 2 en dat zou een
 * formulevariabele of een emoji dubbel laten tellen tegen een tekenlimiet.
 */
export function characterCount(text: string): number {
  return [...text].length
}

/**
 * Het aantal woorden (R-13).
 *
 * Eén implementatie voor de evaluator en voor de meelopende teller van de
 * renderer (M2): een teller die anders telt dan de controle die blokkeert is een
 * bron van onbegrip bij de invuller.
 */
export function wordCount(text: string): number {
  const clipped = text.trim()
  if (clipped === '') return 0
  return clipped.split(/\s+/u).length
}

/** Het aantal woorden over een reeks teksten, voor de sectiedrempel van `korteSectie`. */
export function totalWordCount(texts: readonly (string | undefined)[]): number {
  return texts.reduce((sum, text) => sum + (text === undefined ? 0 : wordCount(text)), 0)
}

/** De extensie van een bestandsnaam in kleine letters, zonder punt, voor `bestandseisen`. */
export function extensionOf(fileName: string): string | undefined {
  const point = fileName.lastIndexOf('.')
  // Een naam die met een punt begint is een naam zonder extensie en geen extensie
  // zonder naam.
  if (point <= 0 || point === fileName.length - 1) return undefined
  return fileName.slice(point + 1).toLowerCase()
}
